import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, UserCheck, MessageSquare, Calendar, LogOut, Palette, BookOpen, FolderOpen, Link2 } from 'lucide-react';
import LightSwitch from './LightSwitch';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function NavBadge({ count }) {
  if (!count) return null;
  return <span className="nav-badge">{count > 99 ? '99+' : count}</span>;
}

export default function Layout() {
  const { agent, logout, patchAgent } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ active_leads: 0, unread_message_clients: 0, upcoming_showings: 0 });
  const [togglingTheme, setTogglingTheme] = useState(false);

  useEffect(() => {
    if (!agent) return undefined;
    const load = () => {
      api.get('/dashboard/needs-attention')
        .then((res) => res.data?.summary && setCounts(res.data.summary))
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60000);
    window.addEventListener('focus', load);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', load);
    };
  }, [agent]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = agent ? `${agent.first_name[0]}${agent.last_name[0]}` : 'A';
  const currentTheme = agent?.dashboard_theme || 'light';
  const themeClass = `theme-${currentTheme}`;

  const toggleTheme = async () => {
    if (togglingTheme) return;
    const next = currentTheme === 'dark' ? 'light' : 'dark';
    setTogglingTheme(true);
    patchAgent({ dashboard_theme: next });
    try {
      await api.patch('/agent-ai-config/theme', { dashboard_theme: next });
    } catch {
      patchAgent({ dashboard_theme: currentTheme });
    } finally {
      setTogglingTheme(false);
    }
  };

  return (
    <div className={`layout ${themeClass}`}>
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>Agents<span>4</span>Agents</h1>
          <p>AI Real Estate Platform</p>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <LayoutDashboard size={16} />
            Dashboard
          </NavLink>
          <NavLink
            to="/properties"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            onClick={(e) => {
              if (window.location.pathname === '/properties') {
                e.preventDefault();
                navigate('/properties', { replace: true, state: { view: 'list', at: Date.now() } });
              }
            }}
          >
            <Building2 size={16} />
            Properties
          </NavLink>
          <NavLink to="/leads" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <Users size={16} />
            Leads
            <NavBadge count={counts.active_leads} />
          </NavLink>
          <NavLink to="/clients" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <UserCheck size={16} />
            Clients
            <NavBadge count={counts.unread_message_clients} />
          </NavLink>
          <NavLink to="/chat" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <MessageSquare size={16} />
            Conversations
          </NavLink>
          <NavLink to="/appointments" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <Calendar size={16} />
            Appointments
            <NavBadge count={counts.upcoming_showings} />
          </NavLink>
          <NavLink to="/branding" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <Palette size={16} />
            Branding
          </NavLink>
          <NavLink to="/knowledge" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <BookOpen size={16} />
            Knowledge
          </NavLink>
          <NavLink to="/listing-files" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <FolderOpen size={16} />
            Listing files
          </NavLink>
          <NavLink to="/resources" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            <Link2 size={16} />
            Resources
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <div className="agent-card">
            <div className="agent-avatar">{initials}</div>
            <div className="agent-info">
              <div className="agent-name">{agent?.first_name} {agent?.last_name}</div>
              <div className="agent-status">Active</div>
            </div>
            <LightSwitch daylight={currentTheme !== 'dark'} onToggle={toggleTheme} />
            <button onClick={handleLogout} className="btn btn-ghost btn-sm" title="Logout" aria-label="Logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}